import { AlertCircle, Loader2 } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';

export function Spinner({ className, ...props }: ComponentProps<'svg'>) {
  return <Loader2 className={cn('h-5 w-5 animate-spin text-brand', className)} aria-label="Loading" {...props} />;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-20 text-sm text-muted" role="status">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export function ErrorNote({ title = 'Something went wrong', children }: { title?: string; children?: ReactNode }) {
  return (
    <div className="mb-5 flex gap-3 rounded-2xl bg-danger-soft px-5 py-4 text-sm text-danger" role="alert">
      <AlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden />
      <div>
        <p className="font-semibold">{title}</p>
        {children ? <p className="mt-0.5 text-[color-mix(in_oklab,var(--danger)_80%,var(--ink))]">{children}</p> : null}
      </div>
    </div>
  );
}

/** An empty list says what would fill it, and how. */
export function EmptyNote({ children, icon, title }: { children: ReactNode; icon?: ReactNode; title?: string }) {
  return (
    <div className="flex flex-col items-center rounded-[1.25rem] bg-paper px-6 py-14 text-center">
      {icon ? <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface text-brand shadow-[0_1px_0_var(--line)]">{icon}</div> : null}
      {title ? <p className="font-display text-lg font-semibold text-ink">{title}</p> : null}
      <p className={cn('max-w-sm text-sm text-muted', title && 'mt-1')}>{children}</p>
    </div>
  );
}

export function PageTitle({
  children,
  description,
  aside,
  compact = false,
}: {
  children: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  /** One line, title and description side by side, for a canvas page that needs the height. */
  compact?: boolean;
}) {
  if (compact) {
    return (
      <header className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 px-1">
        <h1 className="font-display text-2xl leading-tight font-semibold text-ink">{children}</h1>
        {description ? <p className="min-w-0 flex-1 text-sm text-muted">{description}</p> : null}
        {aside}
      </header>
    );
  }
  return (
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4 md:mb-10">
      <div className="min-w-0">
        <h1 className="font-display text-[2rem] leading-[1.1] font-semibold text-ink md:text-[2.6rem]">{children}</h1>
        {description ? <p className="mt-2 max-w-[60ch] text-[0.98rem] text-muted">{description}</p> : null}
      </div>
      {aside}
    </header>
  );
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}
